import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';

const ExamResult = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Proves this browser (or the candidate's results email) belongs to the attempt.
  const token = searchParams.get('t') || '';
  const tokenQuery = `t=${encodeURIComponent(token)}`;
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await api.get(`/exam/result/${attemptId}?${tokenQuery}`);
        setResult(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [attemptId, tokenQuery]);

  const handleReviewAnswers = async () => {
    if (reviewData) {
      setShowReview(true);
      return;
    }

    setLoadingReview(true);
    try {
      const response = await api.get(`/exam/result/${attemptId}/review?${tokenQuery}`);
      setReviewData(response.data);
      setShowReview(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load review');
    } finally {
      setLoadingReview(false);
    }
  };

  const [downloading, setDownloading] = useState('');
  const downloadCertificate = async (level) => {
    setDownloading(level);
    try {
      const r = await api.get(`/certificates/attempt/${attemptId}?${tokenQuery}&level=${level}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificate_of_${level === 'achievement' ? 'Completion' : 'Attendance'}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      let msg = 'Could not download the certificate';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch { /* keep default */ }
      alert(msg);
    } finally {
      setDownloading('');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)'
      }}>
        <div className="card text-center" style={{ padding: '40px' }}>
          <div className="text-lg">Loading results...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)'
      }}>
        <div className="card text-center" style={{ maxWidth: '500px' }}>
          <div style={{ fontSize: '60px', marginBottom: '16px' }}>
            ⚠️
          </div>
          <h1 style={{ marginBottom: '16px' }}>Results Not Available</h1>
          <p className="text-muted mb-4">{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/exam')}>
            Go to Exam Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)'
    }}>
      <div className="card text-center" style={{ maxWidth: '500px', width: '100%' }}>
        <div className="text-center mb-4">
          <img src="/lp-logo.png" alt="LP Logo" style={{ height: '50px', marginBottom: '12px' }} />
          <h2 style={{ color: '#ea580c', fontSize: '18px' }}>LP Learning & Assessment Portal</h2>
        </div>

        <div style={{ fontSize: '60px', marginBottom: '16px' }}>
          {result.passed ? '🎉' : result.results_visible ? '📋' : '✅'}
        </div>

        <h1 style={{ marginBottom: '8px' }}>
          {result.passed ? 'Congratulations!' : result.results_visible ? 'Exam Completed' : 'Submitted Successfully'}
        </h1>

        <p className="text-muted mb-3">{result.candidate_name}</p>

        <div style={{
          background: '#f9fafb',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'left'
        }}>
          <p><strong>Quiz:</strong> {result.quiz_title}</p>
          {result.session_name && <p><strong>Session:</strong> {result.session_name}</p>}
          <p><strong>Submitted:</strong> {new Date(result.submitted_at).toLocaleString()}</p>
        </div>

        {result.results_visible && !result.has_free_text && (
          <div style={{
            background: result.passed ? '#f0fdf4' : '#fff7ed',
            padding: '24px',
            borderRadius: '12px',
            marginBottom: '24px'
          }}>
            <p className="text-muted">Your Score</p>
            <h2 style={{ fontSize: '48px', color: result.passed ? '#10b981' : '#f97316' }}>
              {result.percentage}%
            </h2>
            <p>{result.score} out of {result.total_points} points</p>
            {result.passed && (
              <span className="badge badge-success">PASSED</span>
            )}
            {!result.passed && (
              <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '14px' }}>
                Thank you for completing the assessment. This attempt did not meet the required score. You may retake the assessment when ready.
              </p>
            )}
          </div>
        )}

        {result.has_free_text && (
          <div className="alert alert-info">
            Some of your answers require manual review. Your final score will be determined after the examiner reviews your responses.
          </div>
        )}

        {!result.results_visible && (
          <div className="alert alert-info">
            Your exam has been submitted successfully. Results will be shared by your examiner.
          </div>
        )}

        {(result.certificates || []).length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
            <p style={{ fontWeight: 600, marginBottom: '10px' }}>
              {result.certificates.length > 1 ? 'Your certificates' : 'Your certificate'}
            </p>
            <div className="flex gap-2" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
              {result.certificates.map(c => (c.state === 'revoked'
                ? <p key={c.level} className="text-sm text-muted" style={{ margin: 0 }}>
                    Your {c.level} certificate has been withdrawn. Please contact your trainer.
                  </p>
                : <button key={c.level} className="btn btn-success" onClick={() => downloadCertificate(c.level)}
                    disabled={downloading === c.level}>
                    {downloading === c.level ? 'Preparing...' : `Download ${c.level === 'achievement' ? 'Certificate of Completion' : 'Certificate of Attendance'}`}
                  </button>))}
            </div>
          </div>
        )}

        {result.review_enabled && result.review_open !== false && (
          <div className="flex gap-3" style={{ justifyContent: 'center' }}>
            <button
              className="btn btn-primary btn-lg"
              onClick={handleReviewAnswers}
              disabled={loadingReview}
            >
              {loadingReview ? 'Loading...' : 'Review Answers'}
            </button>
          </div>
        )}

        {/* Held back until everyone's window has closed, so answers cannot be
            passed to colleagues who are still sitting the exam. */}
        {result.review_enabled && result.review_open === false && (
          <div className="alert alert-info" style={{ textAlign: 'center', fontSize: '14px' }}>
            Answers and explanations will be available here after the exam window closes
            {result.review_opens_at && (
              <> — from <strong>{new Date(result.review_opens_at).toLocaleString('en-IN', {
                day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit'
              })}</strong></>
            )}.
            <br />
            <span className="text-muted" style={{ fontSize: '13px' }}>
              Keep this page's link, or use the one in your results email, to come back.
            </span>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReview && reviewData && (
        <div className="modal-overlay" {...overlayDismiss(() => setShowReview(false))}>
          <div
            className="modal"
            style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Answer Review</h3>
              <button className="modal-close" onClick={() => setShowReview(false)}>&times;</button>
            </div>
            <div
              className="modal-body"
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onContextMenu={e => e.preventDefault()}
              onCopy={e => e.preventDefault()}
              onCut={e => e.preventDefault()}
            >
              <div style={{
                background: '#f3f4f6',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <span style={{ fontWeight: '600' }}>
                  Score: {reviewData.score} / {reviewData.total_points} ({reviewData.percentage}%)
                </span>
              </div>

              {reviewData.questions.map((q, index) => {
                const isCorrect = q.is_correct === true;
                const isIncorrect = q.is_correct === false;
                const isUnanswered = q.selected_option_id === null && q.true_false_answer === null && q.free_text_answer === null;

                return (
                  <div
                    key={q.id}
                    style={{
                      marginBottom: '16px',
                      padding: '16px',
                      borderRadius: '8px',
                      border: isCorrect ? '2px solid #10b981' : isIncorrect ? '2px solid #ef4444' : '1px solid #e5e7eb',
                      background: isCorrect ? '#f0fdf4' : isIncorrect ? '#fef2f2' : 'white'
                    }}
                  >
                    <div className="flex flex-between flex-center" style={{ marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: '#374151' }}>
                        Q{index + 1}. {q.question_text}
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: isCorrect ? '#10b981' : isIncorrect ? '#ef4444' : '#9ca3af',
                        color: 'white'
                      }}>
                        {isCorrect ? `+${q.points_earned}` : isUnanswered ? 'Skipped' : '0'} / {q.points}
                      </span>
                    </div>

                    {q.question_type === 'multiple_choice' && q.options && (
                      <div style={{ marginTop: '12px' }}>
                        {q.options.map(opt => {
                          const isSelected = opt.id === q.selected_option_id;
                          const isCorrectOption = opt.is_correct;

                          return (
                            <div
                              key={opt.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 12px',
                                marginBottom: '6px',
                                borderRadius: '6px',
                                background: isCorrectOption ? '#dcfce7' : isSelected ? '#fee2e2' : '#f9fafb',
                                border: isCorrectOption ? '1px solid #10b981' : isSelected ? '1px solid #ef4444' : '1px solid #e5e7eb'
                              }}
                            >
                              <span style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                border: isSelected ? '5px solid' : '2px solid',
                                borderColor: isCorrectOption ? '#10b981' : isSelected ? '#ef4444' : '#d1d5db',
                                marginRight: '10px',
                                flexShrink: 0
                              }} />
                              <span style={{ flex: 1 }}>{opt.option_text}</span>
                              {isCorrectOption && (
                                <span style={{ color: '#10b981', fontWeight: '600', fontSize: '12px' }}>Correct</span>
                              )}
                              {isSelected && !isCorrectOption && (
                                <span style={{ color: '#ef4444', fontWeight: '600', fontSize: '12px' }}>Your Answer</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {q.question_type === 'true_false' && (
                      <div style={{ marginTop: '12px' }}>
                        {[true, false].map(value => {
                          const isSelected = q.true_false_answer === value;
                          const isCorrectOption = q.correct_answer === value;

                          return (
                            <div
                              key={value.toString()}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '8px 16px',
                                marginRight: '12px',
                                borderRadius: '6px',
                                background: isCorrectOption ? '#dcfce7' : isSelected ? '#fee2e2' : '#f9fafb',
                                border: isCorrectOption ? '1px solid #10b981' : isSelected ? '1px solid #ef4444' : '1px solid #e5e7eb'
                              }}
                            >
                              <span style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: isSelected ? '4px solid' : '2px solid',
                                borderColor: isCorrectOption ? '#10b981' : isSelected ? '#ef4444' : '#d1d5db',
                                marginRight: '8px'
                              }} />
                              <span>{value ? 'True' : 'False'}</span>
                              {isCorrectOption && (
                                <span style={{ marginLeft: '8px', color: '#10b981', fontSize: '12px' }}>Correct</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {q.question_type === 'free_text' && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{
                          padding: '12px',
                          background: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <p style={{ margin: 0, color: q.free_text_answer ? '#374151' : '#9ca3af' }}>
                            {q.free_text_answer || 'No answer provided'}
                          </p>
                        </div>
                        <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                          This question was manually graded by the examiner.
                        </p>
                      </div>
                    )}

                    {/* Why the correct answer is correct -- the learning part of an
                        awareness quiz, so it sits directly under the answers. */}
                    {q.explanation && (
                      <div style={{
                        marginTop: '14px', padding: '12px 14px', borderRadius: '6px',
                        background: '#eff6ff', borderLeft: '3px solid #3b82f6'
                      }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#1d4ed8', marginBottom: '4px' }}>
                          Explanation
                        </div>
                        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.55, color: '#1f2937', whiteSpace: 'pre-line' }}>
                          {q.explanation}
                        </p>
                      </div>
                    )}

                    {/* Only present when the quiz opts in to showing trainer notes. */}
                    {q.trainer_note && (
                      <div style={{
                        marginTop: '10px', padding: '12px 14px', borderRadius: '6px',
                        background: '#fefce8', borderLeft: '3px solid #ca8a04'
                      }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#a16207', marginBottom: '4px' }}>
                          Why the other options fail
                        </div>
                        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.55, color: '#1f2937', whiteSpace: 'pre-line' }}>
                          {q.trainer_note}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowReview(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamResult;
